import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap-5';
import Ottercon from 'components/ottercon';
import SearchInput from 'components/search_sidebar/sidebar_search';

import { formatDateTime } from 'helpers/formatters/date_time_formats';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { faClone } from '@fortawesome/pro-regular-svg-icons';

import { deleteRequest, post } from 'helpers/api';
import confirm from 'components/confirm';
import DuplicateModal from './duplicate_modal';

export default function DocTemplateList({
  availableDocumentTemplates,
  duplicateTemplateUrl,
  documentTemplatesLimit
}) {
  const [searchText, setSearchText] = React.useState('');
  const [removedTemplateIDs, setRemovedTemplateIDs] = React.useState([]);
  const [duplicateData, setDuplicateData] = React.useState({ show: false, template: {} });
  const [duplicateSaveError, setDuplicateSaveError] = React.useState(null);
  const docTemplatesLimitReached = () => (
    availableDocumentTemplates.filter(t => t.userGenerated).length >= documentTemplatesLimit
  );
  const canDuplicate = !!duplicateTemplateUrl && !docTemplatesLimitReached();

  const handleUpdateSearchText = (e) => {
    setSearchText(e.target.value);
  };

  const existingDocumentTemplates = () => {
    let templates = [...availableDocumentTemplates];

    if (removedTemplateIDs.length > 0) templates = templates.filter(t => !removedTemplateIDs.includes(t.id));

    return templates;
  };

  const filteredDocumentTemplates = () => {
    let templates = existingDocumentTemplates();

    if (searchText) templates = templates.filter(t => t.name.toLowerCase().includes(searchText.toLowerCase()));

    return templates;
  };

  const handleDelete = (template) => {
    const message = template.createdByName ?
      `Are you sure you want to delete this template created by ${template.createdByName}?` :
      'Are you sure?';

    confirm(message, {
      buttonLabel: 'Delete Document Template',
      buttonStyle: 'danger',
      title: `Delete "${template.name}"?`
    }).then(() => {
      deleteRequest(template.deletePath).then(() => {
        setRemovedTemplateIDs([...removedTemplateIDs, template.id]);
      });
    }).catch(() => {
      // Confirmation cancelled.
    });
  };

  const handleDuplicateOpen = (template) => {
    setDuplicateData({ show: true, template });
  };
  const handleDuplicateClose = () => {
    setDuplicateData({ show: false, template: {} });
    setDuplicateSaveError(null);
  };
  const handleDuplicateSave = ({ duplicateId, newName }) => {
    post(duplicateTemplateUrl, { duplicateId, newName }).then((resp) => {
      if (resp.success) {
        handleDuplicateClose();
        window.location.href = resp.editPath;
      } else {
        setDuplicateSaveError('Sorry, something went wrong when duplicating');
      }
    }).catch(() => {
      setDuplicateSaveError('Sorry, an unexpected error occurred when duplicating');
    });
  };

  const documentTemplates = filteredDocumentTemplates();

  return (
    <div>
      <Row>
        <Col md={6}>
          <SearchInput
            placeholder="Search Documents"
            value={searchText}
            onChange={handleUpdateSearchText}
          />
        </Col>
      </Row>
      {documentTemplates.length > 0 ? (
        <div>
          <h6 className="mt-7 mb-4">All Documents</h6>
          <table className="table">
            <tbody>
              {filteredDocumentTemplates().map(t => (
                <tr key={t.id}>
                  <td className="p-3 align-middle">
                    {/* TODO: unify this logic with the userGenerated attr */}
                    <Ottercon ottercon={t.createdByName ? 'user-doc' : 'doc'} size="md" />
                  </td>
                  <td className="w-100 p-3 align-middle">
                    <div>
                      <strong className="fw-semibold">
                        {t.name}
                      </strong>
                    </div>
                    <div className="text-muted">
                      Last Edited at {formatDateTime(t.updatedAt)}
                    </div>
                  </td>
                  {canDuplicate && (
                    <td className="text-nowrap p-3 align-middle">
                      <button
                        className="btn btn-link p-0 text-decoration-none"
                        data-testid="duplicate-template-button"
                        type="button"
                        onClick={() => handleDuplicateOpen(t)}
                      >
                        <FontAwesomeIcon className="me-2" icon={faClone}/>
                        Duplicate
                      </button>
                    </td>
                  )}
                  <td className="text-nowrap p-3 align-middle">
                    <a href={t.editPath}>
                      <FontAwesomeIcon className="me-2" icon={faEdit}/>
                      Edit Defaults
                    </a>
                    {t.deletePath && (
                      <button
                        aria-label={`Delete ${t.name}`}
                        className="btn btn-link p-0 text-danger ms-4"
                        type="button"
                        onClick={() => handleDelete(t)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} size="lg"/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <DuplicateModal
            existingTemplates={existingDocumentTemplates()}
            saveErrorMessage={duplicateSaveError}
            show={duplicateData.show}
            template={duplicateData.template}
            onHide={handleDuplicateClose}
            onSave={handleDuplicateSave}
          />
        </div>
      ) : (
        <h6 className="mt-7">No Documents Found</h6>
      )}
    </div>
  );
}

DocTemplateList.propTypes = {
  availableDocumentTemplates: PropTypes.arrayOf(PropTypes.shape({
    editPath: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    updatedAt: PropTypes.string.isRequired,
    userGenerated: PropTypes.bool.isRequired
  })),
  documentTemplatesLimit: PropTypes.number.isRequired,
  duplicateTemplateUrl: PropTypes.string,
};
